import { IUser } from '@kaaja/data-models';
import { useIntl } from 'Features/Intl/context';
import { createIntlTranslations } from 'Features/Intl/fns';
import { Button } from 'Features/SRP/Components/Button';
import { Checkbox } from 'Features/SRP/Components/Checkbox';
import { Input } from 'Features/SRP/Components/Input';
import { Label } from 'Features/SRP/Components/Label';
import { Footer } from 'Features/Shared/Components/Footer';
import { Navbar } from 'Features/Shared/Components/Navbar';
import { Select } from 'Features/Shared/Components/UI/Select';
import { config } from 'Shared/Services/environment.service';
import axios from 'axios';
import { createLoopServerClient } from 'loop/client';
import { useLoop } from 'loop/context';
import { GetServerSidePropsContext } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { fetchUserByUsername } from 'v2/http';

interface Props {
  user: IUser;
}

export default function Profile({ user }: Props) {
  const { __ } = useIntl();
  const { replace } = useRouter();
  const { session, signOut } = useLoop();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<{
    givenName: string;
    familyName: string;
    email: string;
    phoneNumber: string;
    privacy: boolean;
    language: string;
    previousPassword: string;
    proposedPassword: string;
  }>({
    defaultValues: {
      givenName: user.name,
      familyName: user.surname,
      email: user.email,
      phoneNumber: user.phoneNumber,
      privacy: Boolean(Number(user.privacy)),
      language: user.language,
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    console.log('Data submitted', data);

    const res = await axios.put(
      '/v2/cognito/me',
      {
        accessToken: session?.accessToken.value,
        attributes: {
          givenName: data.givenName,
          familyName: data.familyName,
          phoneNumber: data.phoneNumber,
          privacy: data.privacy,
          language: data.language,
        },
        previousPassword: data.previousPassword,
        proposedPassword: data.proposedPassword,
      },
      {
        baseURL: config('NX_NEXT_PUBLIC_API_URL'),
        headers: {
          Authorization: `Bearer ${session?.accessToken.value}`,
        },
      },
    );

    if ([200, 201].includes(res.data.status)) {
      toast.success('Operation Successful.');
    } else {
      toast.error(res.data.data.message);
    }
  });

  const onSignout = async () => {
    signOut({ email: user.email })
      .then(() => replace('/v2/signin'))
      .catch(() => toast.error('An error has occurred while signin out.'));
  };

  return (
    <>
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 my-12">
        <div className="md:flex">
          <div className="md:w-96 md:border-r border-gray-100 py-6 md:pr-8">
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              {__('sections.info.welcome', { name: user.name })}
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">{__('sections.info.disclaimer')}</p>
            <Button variant="blue" className="mt-6" onClick={onSignout}>
              {__('sections.info.action')}
            </Button>
          </div>
          <form onSubmit={onSubmit} className="space-y-12 flex-1 md:pl-8 py-6">
            <div>
              <h2 className="text-base font-semibold leading-7 text-gray-900">
                {__('sections.forms.info.title')}
              </h2>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                {__('sections.forms.info.subtitle')}
              </p>
              <div className="mt-10 grid grid-cols-1 gap-6">
                <div className="space-y-2">
                  <Label>{__('sections.forms.info.items.name')}</Label>
                  <Input type="text" {...register('givenName', { required: true })} />
                </div>

                <div className="space-y-2">
                  <Label>{__('sections.forms.info.items.surname')}</Label>
                  <Input type="text" {...register('familyName', { required: true })} />
                </div>

                <div className="space-y-2">
                  <Label>{__('sections.forms.info.items.email')}</Label>
                  <Input type="email" {...register('email', { required: true })} />
                </div>

                <div className="space-y-2">
                  <Label>{__('sections.forms.info.items.mobile')}</Label>
                  <Input type="tel" {...register('phoneNumber', { required: true })} />
                </div>

                <div className="space-y-2">
                  <Label>{__('sections.forms.info.items.language')}</Label>
                  <Select {...register('language', { required: true })}>
                    <option value="it-IT">Italiano</option>
                    <option value="en-US">English</option>
                  </Select>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-base font-semibold leading-7 text-gray-900">
                {__('sections.forms.access.title')}
              </h2>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                {__('sections.forms.access.subtitle')}
              </p>

              <div className="mt-10 grid grid-cols-1 gap-6">
                <div className="space-y-2">
                  <Label>{__('sections.forms.access.items.oldPassword')}</Label>
                  <Input type="password" {...register('previousPassword')} />
                </div>

                <div className="space-y-2">
                  <Label>{__('sections.forms.access.items.newPassword')}</Label>
                  <Input type="password" {...register('proposedPassword')} />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Checkbox {...register('privacy')} />
              <div className="text-sm">{__('sections.forms.marketing')}</div>
            </div>

            <Button variant="blue" type="submit" disabled={isSubmitting}>
              {__('sections.forms.action')}
            </Button>
          </form>
        </div>
      </div>

      <Footer />
    </>
  );
}

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const client = createLoopServerClient(context);
  const session = client.fetchSession?.();

  if (!session) {
    return {
      redirect: {
        permanent: false,
        destination: '/v2/signin',
      },
    };
  }

  const locale = context.locale?.startsWith('it') ? 'it' : 'en';

  const user = await fetchUserByUsername(session.user.id, {
    headers: {
      Authorization: session.accessToken.value,
    },
  });

  const intl = await createIntlTranslations(locale, 'account');
  const i18n = await serverSideTranslations(locale);

  return {
    props: {
      ...intl,
      ...i18n,
      user: user,
    },
  };
};
